import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { switchMap, from, of, take } from 'rxjs';
import { ROUTES } from '../../../shared/constants';
import { OnboardingService } from '../services/onboarding.service';
import { UserContextService } from '../../../shared/services/user-context.service';
import { ViewingContextService } from '../../../shared/services/viewing-context.service';
import { firestoreRetry } from '../../../shared/utils/firestore-retry';

/**
 * Guard for protected app routes.
 * Resolves user context + viewing context before allowing navigation.
 */
export const onboardingGuard: CanActivateFn = () => {
  const router = inject(Router);
  const auth = inject(Auth);
  const onboarding = inject(OnboardingService);
  const userContext = inject(UserContextService);
  const viewingContext = inject(ViewingContextService);
  const injector = inject(EnvironmentInjector);

  return authState(auth).pipe(
    take(1),
    switchMap((user) => {
      if (!user) {
        return of(router.createUrlTree([ROUTES.AUTH.LOGIN]));
      }

      // If already resolved, skip Firestore reads
      if (userContext.resolved()) {
        return of(true);
      }

      // Run inside injection context so AngularFire Firestore calls work correctly
      return from(
        runInInjectionContext(injector, () =>
          resolveAccess(user.uid, auth, onboarding, userContext, viewingContext, router)
        )
      );
    })
  );
};

async function resolveAccess(
  uid: string,
  auth: Auth,
  onboarding: OnboardingService,
  userContext: UserContextService,
  viewingContext: ViewingContextService,
  router: Router
): Promise<boolean | ReturnType<Router['createUrlTree']>> {
  try {
    // Use firestoreRetry to handle token propagation lag on page reload
    const { completed, step } = await firestoreRetry(auth, () =>
      onboarding.resolveOnboardingStep(uid)
    );

    await userContext.resolve(uid, completed);

    // Init viewing context — determines whose data the user sees
    // Only reset if not already initialized (preserves context switcher state)
    if (!viewingContext.targetUid()) {
      viewingContext.initDefault(uid);
    }

    // Load circle access for any user with circle membership (member-only or both)
    if (userContext.isCircleMember()) {
      await userContext.loadCircleAccessForMember(uid);
    }

    // Circle member-only: auto-switch to parent's family if only 1
    if (userContext.isCircleMemberOnly() && !viewingContext.isViewingParent()) {
      viewingContext.autoSwitchIfSingleFamily(userContext.circleAccess());
    }

    if (completed) return true;
    if (userContext.isCircleMember()) return true;

    return router.createUrlTree([ROUTES.ONBOARDING.ROOT, step]);
  } catch (err) {
    console.error('[onboardingGuard] Failed to resolve access, redirecting to login:', err);
    return router.createUrlTree([ROUTES.AUTH.LOGIN]);
  }
}
